'use client';

import {
	type FC,
	type InputHTMLAttributes,
	useId,
	useRef,
	useState,
} from 'react';
import clsx from 'clsx';

import {CheckIcon} from '@/components/icons/check';
import {WarningIcon} from '@/components/icons/warning';
import {type Theme, THEME_DARK, THEME_LIGHT} from '@/utils/types';

type Props = InputHTMLAttributes<HTMLInputElement> & {
	theme?: Theme;
};

export const FormInput: FC<Props> = ({
	theme = THEME_LIGHT,
	className,
	...props
}) => {
	const id = useId();
	const [initialized, setInitialized] = useState<boolean>(!!props.value);
	const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	return (
		<div
			className={clsx(
				'relative',
				theme === THEME_LIGHT
					? 'focus-within:ring-focus'
					: 'focus-within:ring-focus-dark',
				'rounded',
				'-my-1',
				'py-1',
				'-mx-2',
				'px-2',
			)}
		>
			<input
				id={id}
				type="text"
				className={clsx(
					'inline-flex',
					'bg-transparent',
					'py-3',
					'px-0',
					'text-bodyMedium',
					'outline-none',
					'transition-all',
					'duration-200',
					'border-0',
					'border-b',
					'w-full',
					'box-border',
					'peer',
					'focus:ring-disabled',
					'!ring-0',
					'!ring-offset-0',
					'!outline-0',
					initialized && 'invalid:pr-8',
					theme === THEME_LIGHT && [
						'text-forestGreen-700',
						'active:bg-beige-500',
						'active:text-forestGreen-700',
						'placeholder:text-forestGreen-100',
						'!border-forestGreen-700',
					],
					theme === THEME_DARK && [
						'text-turquoise-700',
						'active:bg-forestGreen-700',
						'active:text-turquoise-700',
						'placeholder:text-[#82ABA7]',
						'!border-turquoise-700',
					],
					className,
				)}
				onChange={(event) => {
					if (typingTimeoutRef.current) {
						clearTimeout(typingTimeoutRef.current);
					}
					if (event.target.value.length > 0) {
						typingTimeoutRef.current = setTimeout(() => {
							setInitialized(true);
						}, 500);
					}
				}}
				{...props}
			/>
			<div
				className={clsx(
					'absolute',
					'h-full',
					'py-5',
					'mr-5',
					'right-0',
					'top-0',
					'hidden',
					'text-orange-700',
					initialized && 'peer-invalid:flex',
				)}
			>
				<WarningIcon />
			</div>
			<div
				className={clsx(
					'absolute',
					'h-full',
					'py-5',
					'mr-5',
					'right-0',
					'top-0',
					'hidden',
					'text-green-700',
					initialized && 'peer-valid:flex',
				)}
			>
				<CheckIcon />
			</div>
		</div>
	);
};
